import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  convertPublishedCatalogImage,
  deleteInstitution,
  fetchCatalogSnapshot,
  importAppStoreArtwork,
  saveInstitution,
  uploadCatalogImage,
} from "../lib/catalogApi";
import type { BankType, Institution, InstitutionKind } from "../lib/catalogTypes";
import {
  encodeAdditionalInstitutionKinds,
  parseInstitutionKindsJSON,
} from "../lib/catalogTypes";
import { lookupAppStoreApp, parseAppStoreID } from "../lib/appStoreLookup";
import { isVectorAssetURL } from "../lib/catalogImages";

const KINDS: { value: InstitutionKind; label: string }[] = [
  { value: "bank", label: "Bank" },
  { value: "brokerage", label: "Brokerage" },
  { value: "government", label: "Government" },
  { value: "insurer", label: "Insurer" },
  { value: "transportation", label: "Transportation" },
  { value: "other", label: "Other" },
];

const KIND_LABELS: Record<InstitutionKind, string> = Object.fromEntries(
  KINDS.map((k) => [k.value, k.label]),
) as Record<InstitutionKind, string>;

const BANK_TYPES: { value: BankType; label: string }[] = [
  { value: "digital", label: "Digital Bank" },
  { value: "ewallet", label: "eWallet" },
  { value: "universal", label: "Universal Bank" },
  { value: "thrift", label: "Thrift Bank" },
  { value: "neo", label: "Neo Bank" },
  { value: "cooperative", label: "Cooperative Bank" },
  { value: "rural", label: "Rural Bank" },
  { value: "bnpl", label: "BNPL" },
  { value: "other", label: "Other" },
];

const BANK_TYPE_LABELS: Record<BankType, string> = Object.fromEntries(
  BANK_TYPES.map((b) => [b.value, b.label]),
) as Record<BankType, string>;

type InstitutionDraft = Partial<Institution> & {
  additionalKinds?: InstitutionKind[];
};

function additionalKindsOf(row: Pick<Institution, "kind" | "additionalKindsJSON">): InstitutionKind[] {
  return parseInstitutionKindsJSON(row.additionalKindsJSON).filter((kind) => kind !== row.kind);
}

function kindLabel(row: Institution): string {
  const extras = additionalKindsOf(row)
    .map((kind) => KIND_LABELS[kind] ?? kind)
    .join(", ");
  let primary: string;
  if (row.kind === "bank") {
    const sub = row.bankType ? BANK_TYPE_LABELS[row.bankType] : null;
    primary = sub ? `Bank · ${sub}` : "Bank";
  } else {
    primary = KIND_LABELS[row.kind] ?? row.kind;
  }
  return extras ? `${primary} · also ${extras}` : primary;
}

/** Full payload so bulk edits never drop fields the form isn't touching. */
function institutionPayload(
  row: Institution,
  patch: Partial<InstitutionDraft> = {},
): Institution {
  const next = { ...row, ...patch };
  const kind = next.kind ?? "other";
  const additionalSource =
    patch.additionalKinds ??
    next.additionalKinds ??
    parseInstitutionKindsJSON(next.additionalKindsJSON);
  return {
    ...next,
    kind,
    bankType: kind === "bank" ? next.bankType ?? null : null,
    additionalKindsJSON: encodeAdditionalInstitutionKinds(kind, additionalSource),
  };
}

export function InstitutionsPage() {
  const [rows, setRows] = useState<Institution[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<InstitutionDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkKind, setBulkKind] = useState("");
  const [bulkBankType, setBulkBankType] = useState("");
  const [bulkCountry, setBulkCountry] = useState("");
  const [lookupStatus, setLookupStatus] = useState<string | null>(null);
  const [convertingRecordName, setConvertingRecordName] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const snap = await fetchCatalogSnapshot();
    setRows(snap.institutions);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.name, row.slug, row.countryCode, kindLabel(row), row.appStoreAppID ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const selectedRows = useMemo(
    () => filtered.filter((row) => selected.has(row.recordName)),
    [filtered, selected],
  );
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((row) => selected.has(row.recordName));

  function toggleRow(recordName: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(recordName)) next.delete(recordName);
      else next.add(recordName);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((row) => next.delete(row.recordName));
      else filtered.forEach((row) => next.add(row.recordName));
      return next;
    });
  }

  /** Applies sequential writes so one rejected record surfaces instead of being lost. */
  async function runBatch(
    updates: Array<{ row: Institution; patch: Partial<Institution> }>,
    ok: string,
  ) {
    if (updates.length === 0) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      let done = 0;
      for (const { row, patch } of updates) {
        await saveInstitution(institutionPayload(row, patch));
        done += 1;
      }
      setMessage(`${ok} (${done} updated)`);
      await reload();
    } catch (err) {
      const detail = err instanceof Error ? err.message : "Bulk update failed";
      setError(`${detail} — some records may not have been updated.`);
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  async function onApplyBulk() {
    const patch: Partial<Institution> = {};
    if (bulkKind) {
      patch.kind = bulkKind as InstitutionKind;
      if (bulkKind === "bank" && bulkBankType) patch.bankType = bulkBankType as BankType;
    }
    if (bulkCountry.trim()) patch.countryCode = bulkCountry.trim().toUpperCase();
    if (Object.keys(patch).length === 0) {
      setError("Choose a Kind or Country to apply.");
      return;
    }
    await runBatch(
      selectedRows.map((row) => ({ row, patch })),
      "Bulk update applied",
    );
    setSelected(new Set());
  }

  async function convertExistingLogo(row: Institution) {
    if (!row.logoURL || !isVectorAssetURL(row.logoURL)) return;
    setConvertingRecordName(row.recordName);
    setError(null);
    setMessage(null);
    try {
      const logoURL = await convertPublishedCatalogImage(row.logoURL, "institution");
      await saveInstitution(institutionPayload(row, { logoURL }));
      setMessage(`Converted ${row.name} to an app-ready PNG.`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "SVG conversion failed.");
    } finally {
      setConvertingRecordName(null);
    }
  }

  async function convertAllExistingLogos() {
    const vectors = rows.filter((row) => row.logoURL && isVectorAssetURL(row.logoURL));
    if (vectors.length === 0) {
      setMessage("No institution SVG files need conversion.");
      return;
    }
    setConvertingRecordName("__all__");
    setError(null);
    setMessage(null);
    try {
      let converted = 0;
      for (const row of vectors) {
        const logoURL = await convertPublishedCatalogImage(row.logoURL!, "institution");
        await saveInstitution(institutionPayload(row, { logoURL }));
        converted += 1;
      }
      setMessage(`Converted ${converted} institution SVG file(s) to app-ready PNG.`);
      await reload();
    } catch (err) {
      setError(`${err instanceof Error ? err.message : "SVG conversion failed."} Some records may already be converted.`);
      await reload().catch(() => undefined);
    } finally {
      setConvertingRecordName(null);
    }
  }

  async function onSortAlphabetically() {
    const ordered = [...rows].sort((a, b) =>
      a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }),
    );
    // Only rewrite records whose position actually changed.
    const updates = ordered
      .map((row, index) => ({ row, patch: { sortOrder: index * 10 } }))
      .filter(({ row, patch }) => row.sortOrder !== patch.sortOrder);
    if (updates.length === 0) {
      setMessage("Sort order is already alphabetical.");
      return;
    }
    await runBatch(updates, "Sort order set A–Z");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.name) return;
    const parsedAppStoreID = editing.appStoreURL
      ? parseAppStoreID(editing.appStoreURL)
      : null;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const kind = editing.kind ?? "other";
      const additionalKinds =
        editing.additionalKinds ??
        parseInstitutionKindsJSON(editing.additionalKindsJSON).filter((value) => value !== kind);
      await saveInstitution({
        recordName: editing.recordName,
        slug: editing.slug,
        name: editing.name,
        kind,
        bankType: kind === "bank" ? editing.bankType ?? null : null,
        additionalKindsJSON: encodeAdditionalInstitutionKinds(kind, additionalKinds),
        countryCode: editing.countryCode || "PH",
        appStoreAppID: parsedAppStoreID ?? editing.appStoreAppID ?? null,
        appStoreURL: editing.appStoreURL ?? null,
        logoURL: editing.logoURL ?? null,
        sortOrder: Number(editing.sortOrder ?? 0),
        isActive: editing.isActive !== false,
      });
      setEditing(null);
      setMessage("Institution saved.");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onImportAppStoreArtwork() {
    if (!editing) return;
    const source = editing.appStoreURL?.trim() || editing.appStoreAppID?.trim();
    if (!source) {
      setError("Enter an App Store app ID or URL first.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setLookupStatus("Searching App Store storefronts…");
    try {
      const app = await lookupAppStoreApp(source, editing.countryCode || "PH", {
        onProgress: (checked, total) =>
          setLookupStatus(`Searching App Store storefronts… ${checked}/${total}`),
      });
      setLookupStatus(`Found in the ${app.storefront} store. Caching artwork…`);
      const imported = await importAppStoreArtwork({
        appIDOrURL: app.appStoreAppID,
        countryCode: app.storefront,
        artworkURL: app.artworkURL,
        appStoreURL: app.appStoreURL,
        appName: app.appName,
      });
      setEditing((current) =>
        current
          ? {
              ...current,
              appStoreAppID: imported.appStoreAppID,
              appStoreURL: imported.appStoreURL,
              logoURL: imported.logoURL,
            }
          : current,
      );
      setMessage(
        `Imported ${imported.appName || imported.appStoreAppID} artwork from the ${app.storefront} App Store.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "App Store artwork import failed");
    } finally {
      setLookupStatus(null);
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Institutions</h1>
            <p>
              Banks, e-wallets, brokerages, insurers, transit operators, and similar orgs
              (CloudKit Public). Primary kind + bank type stay as today; mark any extra arms
              under Also offers.
            </p>
          </div>
          <div className="row-actions">
            <button
              className="btn btn-brass"
              type="button"
              disabled={convertingRecordName !== null || !rows.some((row) => isVectorAssetURL(row.logoURL))}
              onClick={() => void convertAllExistingLogos()}
            >
              <Icon name="refresh" size={15} />
              {convertingRecordName === "__all__" ? "Converting SVGs…" : "Convert all SVGs"}
            </button>
            <button
              className="btn btn-brass"
              type="button"
              disabled={busy || rows.length === 0}
              onClick={() => void onSortAlphabetically()}
              title="Renumber every institution A–Z in increments of 10"
            >
              <Icon name="refresh" size={15} />
              Sort A–Z
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() =>
                setEditing({
                  slug: "",
                  name: "",
                  kind: "bank",
                  bankType: "digital",
                  additionalKinds: [],
                  additionalKindsJSON: null,
                  countryCode: "PH",
                  sortOrder: 0,
                  isActive: true,
                })
              }
            >
              <Icon name="plus" size={15} />
              Add institution
            </button>
          </div>
        </div>
        {error && <div className="banner error">{error}</div>}
        {message && <div className="banner success">{message}</div>}

        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <label>
              Slug
              <input
                value={editing.slug ?? ""}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                required
                disabled={Boolean(editing.recordName)}
              />
            </label>
            <label>
              Name
              <input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </label>
            <label>
              Kind
              <select
                value={editing.kind ?? "other"}
                onChange={(e) => {
                  const kind = e.target.value as InstitutionKind;
                  const previousAdditional =
                    editing.additionalKinds ??
                    parseInstitutionKindsJSON(editing.additionalKindsJSON);
                  setEditing({
                    ...editing,
                    kind,
                    bankType: kind === "bank" ? editing.bankType ?? "digital" : null,
                    additionalKinds: previousAdditional.filter((value) => value !== kind),
                  });
                }}
              >
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </label>
            {editing.kind === "bank" && (
              <label>
                Primary bank type
                <select
                  value={editing.bankType ?? "digital"}
                  onChange={(e) =>
                    setEditing({ ...editing, bankType: e.target.value as BankType })
                  }
                >
                  {BANK_TYPES.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <fieldset className="full">
              <legend>Also offers</legend>
              <p className="field-hint" style={{ marginTop: 0 }}>
                Extra lines of business beyond the primary kind (e.g. a universal bank that also
                has brokerage or insurance).
              </p>
              <div className="chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {KINDS.filter((k) => k.value !== (editing.kind ?? "other")).map((k) => {
                  const selected =
                    editing.additionalKinds ??
                    parseInstitutionKindsJSON(editing.additionalKindsJSON).filter(
                      (value) => value !== editing.kind,
                    );
                  const on = selected.includes(k.value);
                  return (
                    <button
                      key={k.value}
                      type="button"
                      className={on ? "btn btn-brass" : "btn btn-secondary"}
                      onClick={() => {
                        const next = new Set(selected);
                        if (on) next.delete(k.value);
                        else next.add(k.value);
                        setEditing({
                          ...editing,
                          additionalKinds: Array.from(next),
                        });
                      }}
                    >
                      {k.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <label>
              Country
              <input
                value={editing.countryCode ?? "PH"}
                onChange={(e) => setEditing({ ...editing, countryCode: e.target.value })}
              />
            </label>
            <label>
              Sort
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>
            <label>
              App Store app ID
              <input
                inputMode="numeric"
                value={editing.appStoreAppID ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, appStoreAppID: e.target.value || null })
                }
                placeholder="1444761746"
              />
            </label>
            <label className="full">
              App Store URL
              <input
                type="url"
                value={editing.appStoreURL ?? ""}
                onChange={(e) => {
                  const appStoreURL = e.target.value;
                  const parsedAppStoreID = parseAppStoreID(appStoreURL);
                  setEditing({
                    ...editing,
                    appStoreURL: appStoreURL || null,
                    appStoreAppID: parsedAppStoreID ?? editing.appStoreAppID ?? null,
                  });
                }}
                placeholder="https://apps.apple.com/ph/app/…/id1444761746"
              />
            </label>
            <div className="row-actions full">
              <button
                className="btn btn-brass"
                type="button"
                disabled={busy || (!editing.appStoreAppID && !editing.appStoreURL)}
                onClick={() => void onImportAppStoreArtwork()}
              >
                <Icon name="upload" size={15} />
                {busy ? "Importing…" : "Fetch App Store icon → R2"}
              </button>
              <span className="field-hint" style={{ alignSelf: "center" }}>
                {lookupStatus ??
                  "Sweeps every App Store storefront (country first); the app loads only the cached R2 image."}
              </span>
            </div>
            <label className="full">
              Logo URL
              <input
                value={editing.logoURL ?? ""}
                onChange={(e) => setEditing({ ...editing, logoURL: e.target.value || null })}
              />
            </label>
            <label>
              Logo file
              <input
                type="file"
                accept=".svg,.png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadCatalogImage(file, "institution")
                    .then((url) => setEditing((prev) => (prev ? { ...prev, logoURL: url } : prev)))
                    .catch((err) => setError(err instanceof Error ? err.message : "Upload failed"));
                }}
              />
              <span className="field-hint">
                SVG becomes a 256px PNG — the app cannot draw vectors.
              </span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={editing.isActive !== false}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <section className="panel">
          <div className="row-actions" style={{ marginBottom: "0.75rem" }}>
            <input
              className="search"
              type="search"
              value={search}
              placeholder="Search name, slug, kind, country…"
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="field-hint" style={{ alignSelf: "center" }}>
              {filtered.length} of {rows.length}
              {selected.size > 0 ? ` · ${selected.size} selected` : ""}
            </span>
          </div>

          <div className="bulk-bar">
            <span className="field-label">Bulk edit {selectedRows.length} selected</span>
            <select value={bulkKind} onChange={(e) => setBulkKind(e.target.value)}>
              <option value="">Kind — leave unchanged</option>
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            {bulkKind === "bank" && (
              <select value={bulkBankType} onChange={(e) => setBulkBankType(e.target.value)}>
                <option value="">Bank type — leave unchanged</option>
                {BANK_TYPES.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            )}
            <input
              value={bulkCountry}
              placeholder="Country — leave blank"
              maxLength={2}
              style={{ maxWidth: 190 }}
              onChange={(e) => setBulkCountry(e.target.value)}
            />
            <button
              className="btn btn-primary"
              type="button"
              disabled={busy || selectedRows.length === 0}
              onClick={() => void onApplyBulk()}
            >
              Apply to selected
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              disabled={busy || selected.size === 0}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>

          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Select all visible institutions"
                  />
                </th>
                <th>Icon</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Kind</th>
                <th>Country</th>
                <th>Sort</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.recordName}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.recordName)}
                      onChange={() => toggleRow(row.recordName)}
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td>
                    {row.logoURL ? (
                      <>
                        <img className="catalog-logo" src={row.logoURL} alt="" />
                        {isVectorAssetURL(row.logoURL) && (
                          <span className="badge warn" title="The app cannot decode SVG. Re-import this logo to publish a PNG.">
                            SVG · blank in app
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>{kindLabel(row)}</td>
                  <td>{row.countryCode}</td>
                  <td>{row.sortOrder}</td>
                  <td className="row-actions">
                    {isVectorAssetURL(row.logoURL) && (
                      <button
                        className="btn btn-brass"
                        type="button"
                        disabled={convertingRecordName !== null}
                        onClick={() => void convertExistingLogo(row)}
                      >
                        {convertingRecordName === row.recordName ? "Converting…" : "Convert to PNG"}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        setEditing({
                          ...row,
                          additionalKinds: additionalKindsOf(row),
                        })
                      }
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        void deleteInstitution(row.recordName)
                          .then(reload)
                          .catch((err) =>
                            setError(err instanceof Error ? err.message : "Delete failed"),
                          )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      </main>
    </AppShell>
  );
}
